import React, { useState } from 'react';
import { 
  FileText, Download, Send, Printer,
  Check, Clock, User, Calendar
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

interface PlanogramPattern {
  id: string;
  name: string;
  description: string;
  expectedSalesIncrease: number;
  expectedProfitIncrease: number;
  changes: number;
  confidence: number;
  rules: string[];
}

interface InstructionGeneratorProps {
  pattern: PlanogramPattern | null;
  store: string;
}

interface Instruction {
  id: string;
  section: string;
  priority: 'high' | 'medium' | 'low';
  tasks: {
    id: string;
    description: string;
    category: string;
    estimatedTime: number; // minutes
    tools: string[];
    completed?: boolean;
  }[];
}

const InstructionGenerator: React.FC<InstructionGeneratorProps> = ({ pattern, store }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedInstructions, setGeneratedInstructions] = useState<Instruction[] | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'excel' | 'print'>('pdf');
  const [assignedStaff, setAssignedStaff] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  
  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setGeneratedInstructions([
        {
          id: 'INS001',
          section: '飲料コーナー',
          priority: 'high',
          tasks: [
            {
              id: 'TASK001',
              description: 'コカ・コーラ500mlを棚2段目の左端から3フェースに拡大',
              category: '飲料',
              estimatedTime: 10,
              tools: ['プライスカード', 'POPスタンド'],
              completed: false
            },
            {
              id: 'TASK002',
              description: 'サントリー天然水2Lを棚4段目に移動（重量商品のため）',
              category: '飲料',
              estimatedTime: 15,
              tools: ['台車'],
              completed: false
            },
            {
              id: 'TASK003',
              description: '季節限定ドリンクをエンドに特設展開',
              category: '飲料',
              estimatedTime: 20,
              tools: ['エンド什器', '季節POP'],
              completed: false
            }
          ]
        },
        {
          id: 'INS002',
          section: '菓子コーナー',
          priority: 'medium',
          tasks: [
            {
              id: 'TASK004',
              description: 'ポテトチップス各種をブランド別にグループ化',
              category: '菓子',
              estimatedTime: 25,
              tools: ['仕切り板'],
              completed: false
            },
            {
              id: 'TASK005',
              description: 'チョコレート菓子を温度管理エリアに集約',
              category: '菓子',
              estimatedTime: 30,
              tools: ['温度計'],
              completed: false
            }
          ]
        },
        {
          id: 'INS003',
          section: '日配品コーナー',
          priority: 'high',
          tasks: [
            {
              id: 'TASK006',
              description: '牛乳・ヨーグルトの賞味期限順の前出し',
              category: '日配品',
              estimatedTime: 20,
              tools: ['日付シール'],
              completed: false
            },
            {
              id: 'TASK007',
              description: '売れ筋商品のフェース拡大（2→3フェース）',
              category: '日配品',
              estimatedTime: 15,
              tools: [],
              completed: false
            }
          ]
        }
      ]);
      setIsGenerating(false);
    }, 2000);
  };
  
  const getTotalTime = () => {
    if (!generatedInstructions) return 0;
    return generatedInstructions.reduce((total, section) => 
      total + section.tasks.reduce((sectionTotal, task) => sectionTotal + task.estimatedTime, 0), 0
    );
  };
  
  const getCompletedTasks = () => {
    if (!generatedInstructions) return 0;
    return generatedInstructions.reduce((total, section) => 
      total + section.tasks.filter(task => task.completed).length, 0
    );
  };
  
  const getTotalTasks = () => {
    if (!generatedInstructions) return 0;
    return generatedInstructions.reduce((total, section) => 
      total + section.tasks.length, 0
    );
  };

  return (
    <div className="space-y-6">
      {/* 設定エリア */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
          陳列指示書生成
        </h3>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              対象店舗
            </label>
            <input 
              type="text"
              className="w-full p-2 border rounded-lg"
              style={{ borderColor: colors.ui.border }}
              value={store === 'shibuya' ? '渋谷店' : store === 'shinjuku' ? '新宿店' : '銀座店'}
              disabled
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              適用パターン
            </label>
            <input 
              type="text"
              className="w-full p-2 border rounded-lg"
              style={{ borderColor: colors.ui.border }}
              value={pattern?.name || '未選択'}
              disabled
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              担当スタッフ
            </label>
            <input 
              type="text"
              className="w-full p-2 border rounded-lg"
              style={{ borderColor: colors.ui.border }}
              placeholder="スタッフ名を入力"
              value={assignedStaff}
              onChange={(e) => setAssignedStaff(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              実施予定日
            </label>
            <input 
              type="date"
              className="w-full p-2 border rounded-lg"
              style={{ borderColor: colors.ui.border }}
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            />
          </div>
        </div>
        
        <Button 
          variant="primary" 
          onClick={handleGenerate}
          disabled={!pattern || isGenerating}
        >
          {isGenerating ? (
            <>
              <Clock size={16} className="animate-spin" />
              生成中...
            </>
          ) : (
            <>
              <FileText size={16} />
              指示書を生成
            </>
          )}
        </Button>
      </Card>
      
      {/* 生成された指示書 */}
      {generatedInstructions && (
        <>
          {/* サマリー */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <FileText size={20} style={{ color: colors.accent.purple }} />
                <div>
                  <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                    {generatedInstructions.length}
                  </div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    セクション
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Check size={20} style={{ color: colors.support.success }} />
                <div>
                  <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                    {getCompletedTasks()}/{getTotalTasks()}
                  </div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    タスク完了
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Clock size={20} style={{ color: colors.accent.blue }} />
                <div>
                  <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                    {Math.floor(getTotalTime() / 60)}時間{getTotalTime() % 60}分
                  </div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    作業時間
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <User size={20} style={{ color: colors.text.secondary }} />
                <div>
                  <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                    {assignedStaff || '未割当'}
                  </div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    担当者
                  </div>
                </div>
              </div>
            </Card>
          </div>
          
          {/* タスクリスト */}
          <Card>
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: colors.ui.border }}>
              <h3 className="font-semibold" style={{ color: colors.text.primary }}>
                作業指示詳細
              </h3>
              <div className="flex gap-2">
                <Button
                  variant={selectedFormat === 'pdf' ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => setSelectedFormat('pdf')}
                >
                  PDF
                </Button>
                <Button
                  variant={selectedFormat === 'excel' ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => setSelectedFormat('excel')}
                >
                  Excel
                </Button>
                <Button
                  variant={selectedFormat === 'print' ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => setSelectedFormat('print')}
                >
                  印刷
                </Button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              {generatedInstructions.map(section => (
                <div key={section.id} className="border rounded-lg" style={{ borderColor: colors.ui.border }}>
                  <div className="p-3 border-b flex items-center justify-between" 
                    style={{ 
                      borderColor: colors.ui.border,
                      backgroundColor: colors.ui.bgSecondary 
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium" style={{ color: colors.text.primary }}>
                        {section.section}
                      </h4>
                      <Badge 
                        variant={section.priority === 'high' ? 'error' : 
                                section.priority === 'medium' ? 'warning' : 'secondary'}
                        size="small"
                      >
                        {section.priority === 'high' ? '優先' : 
                         section.priority === 'medium' ? '通常' : '後回し'}
                      </Badge>
                    </div>
                    <span className="text-sm" style={{ color: colors.text.secondary }}>
                      {section.tasks.reduce((sum, task) => sum + task.estimatedTime, 0)}分
                    </span>
                  </div>
                  
                  <div className="p-3 space-y-2">
                    {section.tasks.map(task => (
                      <div key={task.id} className="flex items-start gap-3">
                        <input 
                          type="checkbox"
                          checked={task.completed}
                          onChange={() => {
                            // タスク完了状態の更新
                          }}
                        />
                        <div className="flex-1">
                          <p className="text-sm" style={{ color: colors.text.primary }}>
                            {task.description}
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-xs" style={{ color: colors.text.secondary }}>
                              <Clock size={12} className="inline mr-1" />
                              {task.estimatedTime}分
                            </span>
                            {task.tools.length > 0 && (
                              <span className="text-xs" style={{ color: colors.text.secondary }}>
                                必要道具: {task.tools.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-4 border-t flex justify-between" style={{ borderColor: colors.ui.border }}>
              <div className="flex gap-2">
                <Button variant="secondary">
                  <Download size={16} />
                  ダウンロード
                </Button>
                <Button variant="secondary">
                  <Printer size={16} />
                  印刷
                </Button>
              </div>
              <Button variant="primary">
                <Send size={16} />
                店舗に送信
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default InstructionGenerator;
